import { supabase, PHOTO_BUCKET } from '@/lib/supabase';
import {
  normalizeInvitationData,
  type GuestbookEntry,
  type InvitationData,
  type MemberStatus,
  type RsvpEntry,
  type ThemeKey,
  type WeddingInvitation,
  type WeddingMember,
} from '@/types/wedding';

export interface SignUpInput {
  email: string;
  password: string;
  name: string;
  phone: string;
}

async function readFunctionError(error: unknown): Promise<string> {
  const fallback = '요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.';
  try {
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.clone === 'function') {
      const body = (await ctx.clone().json()) as { error?: string };
      if (body?.error) return String(body.error);
    }
  } catch {
    /* ignore */
  }
  return fallback;
}

export async function signUpWeddingMember(input: SignUpInput): Promise<{ status: string }> {
  const { data, error } = await supabase.functions.invoke('wedding-signup', { body: input });
  if (error) throw new Error(await readFunctionError(error));
  if (data && (data as { error?: string }).error) {
    throw new Error(String((data as { error?: string }).error));
  }
  return data as { status: string };
}

export async function signIn(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  if (error) throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

export async function fetchMember(uid: string): Promise<WeddingMember | null> {
  const { data, error } = await supabase
    .from('wedding_members')
    .select('*')
    .eq('id', uid)
    .maybeSingle();
  if (error) throw error;
  return (data as WeddingMember) ?? null;
}

export async function listMembers(): Promise<WeddingMember[]> {
  const { data, error } = await supabase
    .from('wedding_members')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as WeddingMember[];
}

export async function updateMemberStatus(id: string, status: MemberStatus): Promise<void> {
  const { error } = await supabase.from('wedding_members').update({ status }).eq('id', id);
  if (error) throw error;
}

export async function deleteMember(id: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('wedding-admin', {
    body: { action: 'delete-member', id },
  });
  if (error) throw new Error(await readFunctionError(error));
  if (data && (data as { error?: string }).error) {
    throw new Error(String((data as { error?: string }).error));
  }
}

/* ---------------- Invitations ---------------- */

function randomSlug(): string {
  const alphabet = 'abcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 8; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

interface InvitationRow {
  id: string;
  owner_id: string;
  slug: string;
  theme: string;
  data: unknown;
  published: boolean;
  created_at: string;
  updated_at: string;
}

function toInvitation(row: InvitationRow): WeddingInvitation {
  return {
    id: row.id,
    owner_id: row.owner_id,
    slug: row.slug,
    theme: (row.theme as ThemeKey) ?? 'classic-dark',
    data: normalizeInvitationData(row.data),
    published: row.published,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export async function fetchMyInvitation(ownerId: string): Promise<WeddingInvitation | null> {
  const { data, error } = await supabase
    .from('wedding_invitations')
    .select('*')
    .eq('owner_id', ownerId)
    .maybeSingle();
  if (error) throw error;
  return data ? toInvitation(data as InvitationRow) : null;
}

export async function fetchInvitationBySlug(slug: string): Promise<WeddingInvitation | null> {
  const { data, error } = await supabase
    .from('wedding_invitations')
    .select('*')
    .eq('slug', slug)
    .eq('published', true)
    .maybeSingle();
  if (error) throw error;
  return data ? toInvitation(data as InvitationRow) : null;
}

/* Creates the member's (single) invitation if it does not exist yet. */
export async function ensureInvitation(ownerId: string): Promise<WeddingInvitation> {
  const existing = await fetchMyInvitation(ownerId);
  if (existing) return existing;

  const payload = {
    owner_id: ownerId,
    slug: randomSlug(),
    theme: 'classic-dark' as ThemeKey,
    data: normalizeInvitationData(null),
    published: true,
  };

  const { data, error } = await supabase
    .from('wedding_invitations')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;
  return toInvitation(data as InvitationRow);
}

export interface SaveInvitationInput {
  theme?: ThemeKey;
  data?: InvitationData;
  published?: boolean;
}

export async function saveInvitation(
  id: string,
  input: SaveInvitationInput,
): Promise<WeddingInvitation> {
  const { data, error } = await supabase
    .from('wedding_invitations')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return toInvitation(data as InvitationRow);
}

/* ---------------- Photos ---------------- */

function fileExt(file: File): string {
  const fromName = file.name.includes('.') ? file.name.split('.').pop() ?? '' : '';
  const clean = fromName.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (clean) return clean;
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  if (file.type === 'image/gif') return 'gif';
  return 'jpg';
}

export async function uploadInvitationPhoto(ownerId: string, file: File): Promise<string> {
  const path = `${ownerId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${fileExt(file)}`;
  const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || 'image/jpeg',
  });
  if (error) throw error;
  const { data } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export async function deleteInvitationPhoto(url: string): Promise<void> {
  const marker = `/${PHOTO_BUCKET}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return;
  const path = url.slice(idx + marker.length);
  if (!path) return;
  const { error } = await supabase.storage.from(PHOTO_BUCKET).remove([path]);
  if (error) throw error;
}

/* ---------------- Background music ---------------- */

function audioExt(file: File): string {
  const fromName = file.name.includes('.') ? file.name.split('.').pop() ?? '' : '';
  const clean = fromName.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (clean) return clean;
  if (file.type.includes('wav')) return 'wav';
  if (file.type.includes('ogg')) return 'ogg';
  if (file.type.includes('m4a') || file.type.includes('mp4')) return 'm4a';
  return 'mp3';
}

export async function uploadWeddingMusic(ownerId: string, file: File): Promise<string> {
  const path = `${ownerId}/music-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${audioExt(file)}`;
  const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || 'audio/mpeg',
  });
  if (error) throw error;
  const { data } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export async function deleteWeddingMusic(url: string): Promise<void> {
  await deleteInvitationPhoto(url);
}

/* ---------------- Guestbook ---------------- */

export async function listGuestbook(invitationId: string): Promise<GuestbookEntry[]> {
  const { data, error } = await supabase
    .from('wedding_guestbook')
    .select('*')
    .eq('invitation_id', invitationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as GuestbookEntry[];
}

export interface GuestbookInput {
  invitationId: string;
  name: string;
  side: string;
  message: string;
}

export async function addGuestbook(input: GuestbookInput): Promise<GuestbookEntry> {
  const { data, error } = await supabase
    .from('wedding_guestbook')
    .insert({
      invitation_id: input.invitationId,
      name: input.name.trim(),
      side: input.side,
      message: input.message.trim(),
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as GuestbookEntry;
}

export async function deleteGuestbook(id: string): Promise<void> {
  const { error } = await supabase.from('wedding_guestbook').delete().eq('id', id);
  if (error) throw error;
}

/* ---------------- RSVP ---------------- */

export async function listRsvp(invitationId: string): Promise<RsvpEntry[]> {
  const { data, error } = await supabase
    .from('wedding_rsvp')
    .select('*')
    .eq('invitation_id', invitationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as RsvpEntry[];
}

export interface RsvpInput {
  invitationId: string;
  name: string;
  side: string;
  phone: string;
  attendance: string;
  guests: string;
  meal: string;
  message: string;
}

export async function addRsvp(input: RsvpInput): Promise<RsvpEntry> {
  const { data, error } = await supabase
    .from('wedding_rsvp')
    .insert({
      invitation_id: input.invitationId,
      name: input.name.trim(),
      side: input.side,
      phone: input.phone.trim(),
      attendance: input.attendance,
      guests: input.guests,
      meal: input.meal,
      message: input.message.trim(),
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as RsvpEntry;
}

export async function deleteRsvp(id: string): Promise<void> {
  const { error } = await supabase.from('wedding_rsvp').delete().eq('id', id);
  if (error) throw error;
}